import { ProviderMatchProviderV9 } from '@kyruus/types';
import React, { useCallback, useState } from 'react';
import { useTheme } from '@emotion/react';

import { getProviderImageUrl } from '../../lib/images';
import { withErrorBoundary } from '../ErrorFallback/with-error-boundary';
import { ProviderImg, ProviderImgWrapper } from './styles';

export interface ProfileImageProps {
  /** The provider to display the image for. */
  provider: ProviderMatchProviderV9;
  /** If the customer config themes the image to be circular (theme.provider_profile_provider_image_border_radius === '50%') */
  isCircle: boolean;
}
interface ImageDimensions {
  isLandscape: boolean;
  imageHeight: number;
  imageWidth: number;
}
const ProfileImage = ({
  provider,
  isCircle
}: ProfileImageProps): React.ReactElement => {
  const theme = useTheme() as any;

  const [imageDimensions, setImageDimensions] =
    useState<ImageDimensions | null>(null);

  const onImgLoad = useCallback(
    (image: React.SyntheticEvent<HTMLImageElement>) => {
      if (
        image !== null &&
        image.currentTarget.naturalWidth !== null &&
        image.currentTarget.naturalHeight !== null
      ) {
        setImageDimensions({
          isLandscape:
            image.currentTarget.naturalWidth >
            image.currentTarget.naturalHeight,
          imageHeight: image.currentTarget.naturalHeight,
          imageWidth: image.currentTarget.naturalWidth
        });
      }
    },
    []
  );

  /**
   * @TODO When the image service darkship is enabled 100%, the custom handling for landscape and circle images
   * can be removed because they will be controlled directly by the theme configs in terms of image width/height and
   * border-radius
   */

  return (
    <ProviderImgWrapper
      isCircle={isCircle}
      imageHeight={imageDimensions?.imageHeight}
      isLandscape={imageDimensions?.isLandscape || false}
    >
      <ProviderImg
        alt={provider.name.full}
        src={getProviderImageUrl(provider)}
        itemProp="image"
        onLoad={onImgLoad}
        isCircle={isCircle}
        isLandscape={imageDimensions?.isLandscape || false}
        desiredWidth={theme.provider_profile_provider_image_width}
        desiredHeight={theme.provider_profile_provider_image_height}
      />
    </ProviderImgWrapper>
  );
};

ProfileImage.displayName = 'ProfileImage';

export default withErrorBoundary(ProfileImage);
